"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import { useRouter } from "next/navigation";
import WishlistHandler from "@components/molecules/WishlistHandler";
import FilterClientWrapper from "@components/molecules/FilterClientWrapper";
import BuyCarCard from "@components/molecules/BuyCarCard";
import BrowseCard from "@components/molecules/BrowseCard";
import FilterSection from "@components/molecules/FilterSection";
import Text from "@components/atoms/Text";
import Loader from "@components/atoms/Loader";
import { BrowseCardProps } from "@components/molecules/BrowseCard/BrowseCard.types";
import {
  carListingService,
  ApiFilterParams,
} from "@services/carListingService";
import {
  ApiCarData,
  AwardTrustmarkerCollection,
} from "@interfaces/car-api.types";
import { useFilter } from "@context/FilterContext";
import { ShortlistCar } from "@lib/redux/slices/shortlistSlice";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import assetLoader from "@utils/internalImageLoder";
import Image from "next/image";
import { useIsMobile } from "@hooks/use-mobile";
import { generateBrowseCards } from "@helpers/server/browseCardServerHelpers";
import { useBrowseCardNavigation } from "@hooks/useBrowseCardNavigation";
import { CarWithBrowseCard } from "@hooks/useBrowseCardInsertion";
import BrandAwardsSection from "@components/molecules/BrandAwardsSection";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { DARK_THEME_APPBAR_CONFIG } from "@utils/appbarConfig";

interface CarBrowsingClientProps {
  initialCars: ApiCarData[];
  initialHasMoreData: boolean;
  initialPageCount?: number;
  collectionSlug?: string;
  cityName: string;
  title?: string;
  isLoggedIn: boolean;
  userId?: string;
  styles: any;
  serverGeneratedBrowseCards: CarWithBrowseCard[]; // Server-generated BrowseCard data
  brandAwards?: AwardTrustmarkerCollection[];
  brandName?: string;
}

const CarBrowsingClient = ({
  initialCars,
  initialHasMoreData,
  initialPageCount,
  collectionSlug,
  cityName,
  title,
  isLoggedIn,
  userId,
  styles,
  serverGeneratedBrowseCards,
  brandAwards,
  brandName,
}: CarBrowsingClientProps) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { state: filterState, filterOptions, isInitialized } = useFilter();
  const { getCurrentSlugFilters } = useBrowseCardNavigation();
  const { showLoader, hideLoader } = useGlobalLoader();

  if(collectionSlug){
    updateAppBar(DARK_THEME_APPBAR_CONFIG);
  }

  // State management
  const [allCars, setAllCars] = useState<ApiCarData[]>(initialCars);
  const [currentFilters, setCurrentFilters] = useState<ApiFilterParams>({});
  const [hasMoreData, setHasMoreData] = useState(initialHasMoreData);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(24);
  const [totalPageCount, setTotalPageCount] = useState(initialPageCount);
  const [carsWithBrowseCards, setCarsWithBrowseCards] = useState<
    CarWithBrowseCard[]
  >(serverGeneratedBrowseCards);
  const [currentPage, setCurrentPage] = useState(1);

  // Refs for optimization
  const hasInitializedRef = useRef(false);
  const initialFiltersRef = useRef<string>("");
  const hasEverAppliedFiltersRef = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  // Memoized values to prevent unnecessary recalculations
  const filterSignature = useMemo(() => {
    return JSON.stringify({
      appliedFilters: filterState.appliedFilters || {},
      collectionSlug,
      cityName,
    });
  }, [filterState.appliedFilters, collectionSlug, cityName]);

  const cleanTitle = useCallback((originalTitle: string) => {
    return originalTitle.replace(/\s+Cars\s*/g, " ").trim();
  }, []);

  const modifyBrowseCardTitle = useCallback(
    (browseData: BrowseCardProps): BrowseCardProps => {
      if (!title || !collectionSlug) return browseData;

      const baseTitle = browseData.title.replace("Browse cars by ", "");
      const cleanedTitle = cleanTitle(title);
      return {
        ...browseData,
        title: `Browse ${cleanedTitle} cars by ${baseTitle}`,
        parentTitle: title,
      };
    },
    [title, collectionSlug, cleanTitle]
  );

  // Optimized BrowseCard generation - only recalculate when necessary
  useEffect(() => {
    const newCarsWithBrowseCards = generateBrowseCards(
      allCars,
      filterOptions,
      getCurrentSlugFilters(),
      isMobile
    );
    setCarsWithBrowseCards(newCarsWithBrowseCards);
  }, [allCars, filterOptions, isMobile, getCurrentSlugFilters]);

  // Optimized filter data fetching
  useEffect(() => {
    const fetchFilteredCarData = async () => {
      if (!filterOptions || !isInitialized) return;

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        initialFiltersRef.current = filterSignature;

        const hasUrlFilters = Object.entries(
          filterState.appliedFilters || {}
        ).some(([key, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          if (
            key === "budget" &&
            value &&
            typeof value === "object" &&
            "min" in value &&
            "max" in value
          ) {
            const defaultMin =
              filterOptions?.budget?.default_min_price || 300000;
            const defaultMax =
              filterOptions?.budget?.default_max_price || 30000000;
            return value.min !== defaultMin || value.max !== defaultMax;
          }
          return value !== false && value !== undefined;
        });

        const initialApiFilters = carListingService.transformFilters(
          filterState,
          filterOptions
        );
        if (collectionSlug) {
          initialApiFilters.slug = collectionSlug;
        }
        setCurrentFilters(initialApiFilters);

        if (hasUrlFilters || initialCars.length === 0) {
          hasEverAppliedFiltersRef.current = true;
        } else {
          return;
        }
      }

      const filtersHaveChanged = filterSignature !== initialFiltersRef.current;

      if (filtersHaveChanged) {
        hasEverAppliedFiltersRef.current = true;
      }

      if (
        !filtersHaveChanged &&
        !hasEverAppliedFiltersRef.current &&
        initialCars.length > 0
      ) {
        return;
      }

      setIsLoadingMore(false);
      setIsFilterLoading(true);
      isLoadingRef.current = false;

      const apiFilters = carListingService.transformFilters(
        filterState,
        filterOptions
      );

      if (collectionSlug) {
        apiFilters.slug = collectionSlug;
      }

      setCurrentFilters(apiFilters);

      try {
        const result = await carListingService.getCarList(
          0,
          24,
          cityName,
          apiFilters,
          userId
        );

        const { cars, totalCount, pageCount }: any = result;

        flushSync(() => {
          setAllCars(cars || []);
          setCurrentOffset(24);
          setCurrentPage(1);
          setTotalPageCount(pageCount);

          let newHasMoreData = false;
          if (pageCount != null) {
            newHasMoreData = 1 < pageCount;
          } else if (totalCount != null) {
            newHasMoreData = 24 < totalCount;
          }

          setHasMoreData(newHasMoreData);
          setIsLoadingMore(false);
          setIsFilterLoading(false);
        });
      } catch (error) {
        console.error("Error fetching car data:", error);
        flushSync(() => {
          setAllCars([]);
          setHasMoreData(false);
          setIsLoadingMore(false);
          setIsFilterLoading(false);
        });
      }
    };

    fetchFilteredCarData();
  }, [
    filterSignature,
    filterOptions,
    isInitialized,
    collectionSlug,
    cityName,
    initialCars.length,
    userId,
  ]);

  // Optimized load more function
  const loadMoreData = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreData) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;

      const result = await carListingService.getCarList(
        currentOffset,
        24,
        cityName,
        currentFilters,
        userId
      );

      const { cars: additionalCars, totalCount, pageCount }: any = result;

      if (additionalCars && additionalCars.length > 0) {
        const prevCarIds = new Set(
          allCars.map((car) => `${car.brand_id}-${car.model_id}`)
        );
        const uniqueAdditionalCars = additionalCars.filter(
          (car: any) => !prevCarIds.has(`${car.brand_id}-${car.model_id}`)
        );

        if (uniqueAdditionalCars.length > 0) {
          const newCars = [...allCars, ...uniqueAdditionalCars];
          const newOffset = currentOffset + additionalCars.length;

          let newHasMoreData = false;
          const currentPageCount = pageCount ?? totalPageCount;
          if (currentPageCount != null) {
            newHasMoreData = nextPage < currentPageCount;
          } else if (totalCount != null) {
            newHasMoreData = newOffset < totalCount;
          }

          flushSync(() => {
            setAllCars(newCars);
            setCurrentOffset(newOffset);
            setCurrentPage(nextPage);
            setTotalPageCount(currentPageCount);
            setHasMoreData(newHasMoreData);
            setIsLoadingMore(false);
          });
        } else {
          flushSync(() => {
            setHasMoreData(false);
            setIsLoadingMore(false);
          });
        }
      } else {
        flushSync(() => {
          setHasMoreData(false);
          setIsLoadingMore(false);
        });
      }
    } catch (error) {
      console.error("Error fetching more car data:", error);
      flushSync(() => {
        setHasMoreData(false);
        setIsLoadingMore(false);
      });
    } finally {
      isLoadingRef.current = false;
    }
  }, [
    currentOffset,
    cityName,
    currentFilters,
    allCars,
    hasMoreData,
    currentPage,
    totalPageCount,
    userId,
  ]);

  // Optimized intersection observer
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger || !hasMoreData) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingRef.current && hasMoreData) {
          loadMoreData();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreData, loadMoreData]);

  // Memoized event handlers
  const handleSlugChange = useCallback(
    (newSlug: string) => {
      showLoader("Loading...");
      router.push(`/collection/${newSlug}`);
    },
    [showLoader, router]
  );

  const handleNavigate = useCallback(
    (slug: string) => {
      showLoader("Loading...");
      router.push(`/cars/${slug}`);
    },
    [showLoader, router]
  );

  const handleSpinnerLoading = useCallback(
    (loading: boolean) => {
      showLoader("Loading...");
    },
    [showLoader]
  );

  // Memoized render item function
  const renderItem = useCallback(
    (
      item: any,
      index: number,
      { handleWishlistToggle, isCarWishlisted }: any
    ) => {
      try {
        if (item.type === "car") {
          const carData = item.data as ApiCarData;

          const car: ShortlistCar & { status?: any } = {
            id: carData.model_slug,
            make: carData.brand_name,
            name: carData.model_name,
            status: carData.status,
          };

          return (
            <div
              key={item.id}
              data-car-card
              data-item-index={index}
              style={{ display: "contents" }}
            >
              <BuyCarCard
                isLoggedIn={isLoggedIn}
                cityName={cityName}
                collectionName={title || ""}
                isWishlisted={isCarWishlisted(car.id)}
                onWishlistClick={() =>
                  handleWishlistToggle(
                    carData.brand_name,
                    carData.model_name,
                    car
                  )
                }
                onNavigate={handleNavigate}
                handleSpinnerLoading={handleSpinnerLoading}
                title={title}
                {...carData}
              />
            </div>
          );
        } else {
          const browseData = modifyBrowseCardTitle(
            item.data as BrowseCardProps
          );
          return (
            <div
              key={item.id}
              className={styles.browseCardWrapper}
              data-browse-card
              data-item-index={index}
            >
              <BrowseCard {...browseData} onSlugChange={handleSlugChange} />
            </div>
          );
        }
      } catch (error) {
        console.error("Error rendering item:", error);
        return null;
      }
    },
    [
      isLoggedIn,
      cityName,
      title,
      handleNavigate,
      handleSpinnerLoading,
      modifyBrowseCardTitle,
      handleSlugChange,
      styles.browseCardWrapper,
    ]
  );

  return (
    <>
      <WishlistHandler cityName={cityName}>
        {({ handleWishlistToggle, isCarWishlisted }) => (
          <FilterClientWrapper>
            <div className={styles.innerContainer}>
              {brandAwards && brandAwards.length > 0 && (
                <div className={styles.mobileAwards}>
                    <BrandAwardsSection
                      awards={brandAwards}
                      brandName={brandName}
                    />
                </div>
              )}
              <div className={styles.filterSection}>
                <FilterSection
                  cityName={cityName}
                  collectionName={title || ""}
                />
              </div>
              <div className={styles.cardsGridWrapper}>
                <div className={styles.desktopAwards}>
                  {brandAwards && brandAwards.length > 0 && (
                    <BrandAwardsSection
                      awards={brandAwards}
                      brandName={brandName}
                    />
                  )}
                </div>
                <div ref={gridRef} className={styles.cardsGrid} data-cards-grid>
                  {isFilterLoading ? (
                    <div className={styles.loadingMoreSection}>
                      <Loader
                        text="Finding the best cars for you..."
                        theme="light"
                        variant="center"
                        size="medium"
                      />
                    </div>
                  ) : carsWithBrowseCards.length > 0 ? (
                    <>
                      {carsWithBrowseCards.map((item, index) => (
                        <div key={item.id} style={{ display: "contents" }}>
                          {renderItem(item, index, {
                            handleWishlistToggle,
                            isCarWishlisted,
                          })}
                        </div>
                      ))}

                      {hasMoreData && (
                        <div
                          ref={triggerRef}
                          style={{
                            height: "1px",
                            width: "100%",
                            visibility: "hidden",
                            contain: "layout style",
                            position: "relative",
                          }}
                        />
                      )}

                      {isLoadingMore && hasMoreData && (
                        <div className={styles.loadingMoreSection}>
                          <Loader
                            text="Loading more cars..."
                            theme="light"
                            variant="inline"
                            size="medium"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.notAvailableContainer}>
                      <Image
                        src={`${assetLoader()}/icons/No_cars_available.svg`}
                        alt="No cars available"
                        fill
                        loading="lazy"
                        className={styles.notAvailableIcon}
                      />
                      <Text className={styles.notAvailableTest}>
                        No cars available.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </FilterClientWrapper>
        )}
      </WishlistHandler>
    </>
  );
};

export default CarBrowsingClient;
